import React, { useEffect, useState } from 'react';
import {
    Box, Card, CardContent, Chip, CircularProgress, Container,
    Grid, Typography
} from '@material-ui/core';
import { Description as DescriptionIcon } from '@material-ui/icons';
import { useAuth } from '../../hooks/auth';
import PageTitle from '../../components/ui/PageTitleUI';
import { DocumentationProcess } from '../../interfaces/Documentation';
import DocumentationApi from '../../services/DocumentationApi';
import permissions from '../../utils/Permissions';
import ProcessDetail from './components/ProcessDetail';
import { useStyles } from './Style';

const Documentation: React.FC = () => {
    const classes = useStyles();
    const { hasPermission, user } = useAuth();
    const [processes, setProcesses] = useState<DocumentationProcess[]>([]);
    const [selectedProcess, setSelectedProcess] = useState<DocumentationProcess | null>(null);
    const [loading, setLoading] = useState(false);

    const userPlantId = user?.company?.plantID || '';

    useEffect(() => {
        if (hasPermission(permissions.DOCUMENTATION.View)) {
            loadProcesses();
        }
    }, []);

    const loadProcesses = async () => {
        setLoading(true);
        try {
            const response = await DocumentationApi.getAllProcesses(userPlantId);
            const data = response?.data ?? response;
            setProcesses(Array.isArray(data) ? data : []);
        } catch (error) {
            setProcesses([]);
        } finally {
            setLoading(false);
        }
    };

    const handleProcessSelect = (process: DocumentationProcess) => {
        setSelectedProcess(process);
    };

    if (!hasPermission(permissions.DOCUMENTATION.View)) {
        return (
            <Box className={classes.noPermission}>
                <DescriptionIcon style={{ fontSize: 80, color: '#ccc' }} />
                <Typography variant="h6" style={{ marginTop: 16, color: '#666' }}>
                    Você não tem permissão para acessar a Documentação.
                </Typography>
            </Box>
        );
    }

    const groupedByModule = processes.reduce<Record<string, DocumentationProcess[]>>((acc, process) => {
        if (!acc[process.module]) {
            acc[process.module] = [];
        }
        acc[process.module].push(process);
        return acc;
    }, {});

    return (
        <Container maxWidth="xl" className={classes.container}>
            <Box className={classes.header}>
                <PageTitle title="Documentação de Processos" />
                <Typography variant="body2" color="textSecondary">
                    Documentação estruturada dos processos do sistema MVM, gerada automaticamente com base nas regras e fluxos existentes.
                </Typography>
                {user?.company?.companyName && (
                    <Chip
                        label={`Planta: ${user.company.companyName}`}
                        color="primary"
                        variant="outlined"
                        size="small"
                        style={{ marginTop: 8 }}
                    />
                )}
            </Box>

            {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : selectedProcess ? (
                <Box>
                    <Typography
                        variant="body2"
                        color="primary"
                        style={{ cursor: 'pointer', marginBottom: 16, fontWeight: 600 }}
                        onClick={() => setSelectedProcess(null)}
                    >
                        ← Voltar para lista de módulos
                    </Typography>
                    <Card>
                        <CardContent>
                            <ProcessDetail process={selectedProcess} />
                        </CardContent>
                    </Card>
                </Box>
            ) : (
                <Grid container spacing={3}>
                    {Object.entries(groupedByModule).map(([moduleName, moduleProcesses]) => (
                        <Grid item xs={12} md={6} key={moduleName}>
                            <Card>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={2}>
                                        <DescriptionIcon style={{ marginRight: 8, color: '#597DDA' }} />
                                        <Typography variant="h6" style={{ fontWeight: 600 }}>
                                            {moduleName}
                                        </Typography>
                                    </Box>
                                    {moduleProcesses.map((process) => (
                                        <Box
                                            key={process.processId}
                                            style={{ padding: '8px 0', borderBottom: '1px solid #e6e6e6', cursor: 'pointer' }}
                                            onClick={() => handleProcessSelect(process)}
                                        >
                                            <Typography
                                                variant="body1"
                                                style={{ color: '#464feb', fontWeight: 500 }}
                                            >
                                                {process.processName}
                                            </Typography>
                                            <Typography variant="body2" color="textSecondary">
                                                {process.overview.substring(0, 100)}...
                                            </Typography>
                                        </Box>
                                    ))}
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                    {processes.length === 0 && (
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary" align="center">
                                Nenhum processo documentado encontrado.
                            </Typography>
                        </Grid>
                    )}
                </Grid>
            )}
        </Container>
    );
};

export default Documentation;
